import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { Download, FileText, Loader2, X, BookOpen } from "lucide-react";
import { api, API, assetUrl, fmtDuration, mergeBranding } from "@/lib/api";
import { PublicError } from "@/components/PublicShell";
import { ChatWidget } from "@/components/ChatWidget";
import { useFonts } from "@/components/LandingPage";
import { HubQuestionCard } from "@/components/HubQuestions";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const post = (url, body) => fetch(`${API}${url}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true }).catch(() => {});

function useReadingTracker(sessionId, page, total) {
  const secs = useRef(0);
  const lastAct = useRef(Date.now());
  const st = useRef({ page, total });
  st.current = { page, total };
  const [read, setRead] = useState(0);
  useEffect(() => {
    if (!sessionId) return;
    secs.current = 0;
    const act = () => { lastAct.current = Date.now(); };
    ["scroll", "mousemove", "keydown", "touchstart", "wheel"].forEach((e) => window.addEventListener(e, act, { passive: true, capture: true }));
    const send = (extra = {}) => post("/public/reading/track", { session_id: sessionId, read_seconds: secs.current, page: st.current.page, total_pages: st.current.total, ...extra });
    let t = 0;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible" && Date.now() - lastAct.current < 60000) { secs.current += 1; setRead(secs.current); }
      if (++t % 5 === 0) send();
    }, 1000);
    const flush = () => send();
    window.addEventListener("pagehide", flush);
    return () => { clearInterval(iv); flush(); window.removeEventListener("pagehide", flush);
      ["scroll", "mousemove", "keydown", "touchstart", "wheel"].forEach((e) => window.removeEventListener(e, act, { capture: true })); };
  }, [sessionId]);
  return { read, markDownload: () => post("/public/reading/track", { session_id: sessionId, read_seconds: secs.current, page: st.current.page, total_pages: st.current.total, downloaded: true }) };
}

export default function Reader() {
  const { slug } = useParams();
  const leadId = localStorage.getItem(`sv_lead_${slug}`);
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [c, setC] = useState(null);
  const [email, setEmail] = useState("");
  const [assetId, setAssetId] = useState(null);
  const [sid, setSid] = useState(null);
  const [err, setErr] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(800);
  const [rec, setRec] = useState(null);
  const shown = useRef(new Set());
  const boxRef = useRef(null);

  const open = useCallback((aid) => {
    setSid(null); setNumPages(0); setPage(1); setRec(null);
    api.post(`/public/campaigns/${slug}/reading/start`, { lead_id: leadId, asset_id: aid })
      .then((r) => { setC(r.data.campaign); setEmail(r.data.email); setAssetId(aid); setSid(r.data.session_id); document.title = r.data.campaign.title; window.scrollTo(0, 0); })
      .catch((e) => setErr(e.response?.status || 500));
  }, [slug, leadId]);

  useEffect(() => {
    if (!leadId) return;
    api.get(`/public/campaigns/${slug}`).then((r) => open(r.data.assets[0]?.id)).catch(() => setErr(404));
  }, [slug, leadId, open]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.min(el.clientWidth - 2, 1000)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [c]);

  useEffect(() => {
    if (!numPages) return;
    const onScroll = () => {
      const mid = window.innerHeight / 2;
      let cur = 1;
      document.querySelectorAll("[data-page]").forEach((n) => { if (n.getBoundingClientRect().top < mid) cur = Number(n.dataset.page); });
      setPage(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [numPages, width]);

  const { read, markDownload } = useReadingTracker(sid, page, numPages);
  useEffect(() => {
    if (sid && sessionStorage.getItem(`sv_dl_${slug}`)) { sessionStorage.removeItem(`sv_dl_${slug}`); markDownload(); }
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const b = mergeBranding(c?.branding);
  useFonts(b.font_heading, b.font_body);

  const others = (c?.assets || []).filter((a) => a.id !== assetId);
  useEffect(() => {
    if (!c || !read || rec) return;
    const step = c.recommend_after_seconds || 180;
    if (read % step !== 0) return;
    const next = others.find((a) => !shown.current.has(a.id));
    if (next) { shown.current.add(next.id); setRec(next); }
  }, [read]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (embed && numPages > 0 && page === numPages) window.parent.postMessage({ type: "sv-asset-done", slug }, window.location.origin);
  }, [embed, page, numPages, slug]);

  if (!leadId || err === 403) return <Navigate to={`/c/${slug}`} replace />;
  if (err) return <PublicError text="This content isn't available." />;
  if (!c || !assetId) return <div className="min-h-screen grid place-items-center bg-[#070709]"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>;

  const asset = c.assets.find((a) => a.id === assetId);
  const light = b.theme === "light";
  const accent = b.accent;
  const panel = light ? "bg-white border-slate-200" : "bg-white/[0.04] border-white/10";
  const dim = light ? "text-slate-500" : "text-slate-400";

  return (
    <div className={`min-h-screen ${light ? "text-slate-900" : "text-white"}`} style={{ backgroundColor: b.bg_color, fontFamily: `"${b.font_body}", sans-serif` }}>
      <header className={`sticky top-0 z-30 border-b backdrop-blur-xl ${light ? "bg-white/85 border-slate-200" : "bg-black/60 border-white/10"}`}>
        <div className="max-w-[1400px] mx-auto px-5 h-16 flex items-center gap-4">
          {b.logo_path ? <img src={assetUrl(b.logo_path)} alt="Logo" className="h-8 w-auto object-contain" data-testid="hub-logo" /> : <BookOpen className="w-5 h-5" style={{ color: accent }} />}
          <div className="min-w-0 flex-1">
            <p className="font-semibold truncate" style={{ fontFamily: `"${b.font_heading}", sans-serif` }} data-testid="hub-title">{c.title}</p>
            {c.hub_message && <p className={`text-xs truncate ${dim}`} data-testid="hub-message">{c.hub_message}</p>}
          </div>
          <span className={`hidden md:inline font-mono text-xs ${dim}`} data-testid="reader-progress">Page {page}/{numPages || "…"} · {fmtDuration(read)}</span>
          <a data-testid="reader-download-button" href={`${assetUrl(asset.file_path)}?download=true`} onClick={markDownload}
            className="h-9 px-4 rounded-full text-white text-sm font-medium flex items-center gap-2 hover:opacity-90" style={{ background: accent }}>
            <Download className="w-4 h-4" /> Download
          </a>
        </div>
      </header>

      <div className={`max-w-[1400px] mx-auto px-5 py-6 grid gap-6 ${embed ? "" : "lg:grid-cols-[300px_minmax(0,1fr)]"}`}>
        {!embed && <aside className="lg:sticky lg:top-24 self-start space-y-3 order-2 lg:order-1" data-testid="hub-recommendations">
          <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${dim}`}>Now reading</p>
          <div className={`rounded-xl border p-4 ${panel}`} style={{ boxShadow: `inset 3px 0 0 ${accent}` }}>
            <p className="font-semibold text-sm" data-testid="reader-current-title">{asset.title}</p>
            {asset.description && <p className={`text-xs mt-1 ${dim}`}>{asset.description}</p>}
          </div>
          {others.length > 0 && <p className={`font-mono text-[11px] uppercase tracking-[0.2em] pt-3 ${dim}`}>Recommended reading</p>}
          {others.map((a) => (
            <button key={a.id} data-testid={`hub-rec-${a.id}`} onClick={() => open(a.id)}
              className={`w-full text-left rounded-xl border p-4 transition-colors hover:border-current ${panel}`}>
              <p className="font-medium text-sm flex items-start gap-2"><FileText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} /> {a.title}</p>
              {a.description && <p className={`text-xs mt-1 pl-6 ${dim}`}>{a.description}</p>}
            </button>
          ))}
          {email && !email.endsWith("@anonymous.invalid") && <p className={`text-[11px] pt-2 ${dim}`}>Reading as {email}</p>}
        </aside>}

        <main ref={boxRef} className="order-1 lg:order-2 min-w-0" data-testid="reader-document">
          <Document file={assetUrl(asset.file_path)} onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="py-32 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} /></div>}
            error={<p className="py-20 text-center text-rose-400" data-testid="reader-error">Could not load this document. Use Download instead.</p>}>
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} data-page={i + 1} className="mb-4 flex justify-center" style={{ minHeight: width * 1.3 }}>
                <Page pageNumber={i + 1} width={width} className="shadow-2xl rounded overflow-hidden" />
              </div>
            ))}
          </Document>
        </main>
      </div>

      {rec && !embed && (
        <div data-testid="reader-timed-recommendation" className={`fixed bottom-24 left-5 z-40 w-80 rounded-2xl border p-5 shadow-2xl sv-rise ${light ? "bg-white border-slate-200" : "bg-[#141A24] border-white/10"}`}>
          <button data-testid="reader-rec-close" onClick={() => setRec(null)} className={`absolute top-3 right-3 ${dim}`}><X className="w-4 h-4" /></button>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: accent }}>You might also like</p>
          <p className="font-semibold mb-1">{rec.title}</p>
          {rec.description && <p className={`text-xs mb-4 ${dim}`}>{rec.description}</p>}
          <button data-testid="reader-rec-open" onClick={() => open(rec.id)} className="h-9 px-4 rounded-full text-white text-sm font-medium" style={{ background: accent }}>Read now</button>
        </div>
      )}
      {!rec && <HubQuestionCard slug={slug} leadId={leadId} seconds={read} accent={accent} light={light} />}
      <ChatWidget slug={slug} chat={c.chat} leadId={leadId} assetId={assetId} />
    </div>
  );
}
